import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import {
  addPosReserve, posPackSize, posReserveUnits,
  posUnitsFromEntry, describePosUnits, posUnitNoun, posPackNoun,
} from '../utils/posUtil';
import { spinnerRing } from '../utils/bookingUtils';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import '../css/Pos.css';

/**
 * Records a delivery into the storeroom.
 *
 * ASKS FOR WHAT ARRIVED, NOT THE NEW TOTAL — the same rule as PosAddStockModal
 * and for the same reason. "Four boxes came in" is what the person holding the
 * delivery note knows; "there are now nine boxes" is a sum that silently
 * assumes nobody refilled the fridge while they were counting. addPosReserve
 * adds to whatever the database holds at the moment it commits, so a
 * replenishment happening in the next room cannot be erased by this.
 *
 * This is the counterpart to the reserve field on the product form, which sets
 * an exact figure — a stocktake. Two entries, two different claims, two
 * different rows on the audit trail.
 *
 * @param {object} props
 * @param {object} props.product - needs `id`, `name`, the pack size and nouns
 * @param {(newTotal: number) => void} [props.onAdded] - the reserve after the
 *   delivery, so the form behind can show it without re-reading
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
function PosAddReserveModal({ product, onAdded, onClose }) {
  const { clientId } = useConfig();
  const { staffEmail } = usePosSession();
  const [packs, setPacks] = useState('');
  const [units, setUnits] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const packSize = posPackSize(product);
  const current = posReserveUnits(product);
  const arriving = posUnitsFromEntry({ packs, units }, packSize);

  const handleAdd = async () => {
    if (saving || arriving < 1) return;
    setSaving(true);
    setError('');
    try {
      const after = await addPosReserve(clientId, product, { packs, units }, staffEmail);
      if (onAdded) onAdded(after);
      onClose();
    } catch (err) {
      setError(describeWriteError(err, {
        what: 'this delivery',
        path: `posTenants/${clientId}/products`,
      }));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    // No backdrop click-to-close: this holds unsaved input (CLAUDE.md >
    // Behavior). The × and Cancel are the ways out.
    <div className="pos-modal-overlay pos-modal-overlay-stacked">
      <div className="pos-modal pos-modal-narrow" role="dialog" aria-modal="true" aria-label="Add supplies to the reserve">
        <header className="pos-modal-head">
          <h3>Add supplies</h3>
          <button type="button" className="pos-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pos-modal-body">
          <p className="pos-replenish-lead">{product?.name}</p>

          <div className="pos-replenish-now">
            <div>
              <span>In reserve now</span>
              <strong>{describePosUnits(current, product)}</strong>
            </div>
          </div>

          <div className="pos-replenish-entry">
            {packSize > 0 && (
              <label className="pos-field">
                <span>{posPackNoun(product, 2)} that arrived</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={packs}
                  onChange={(e) => setPacks(e.target.value)}
                  disabled={saving}
                  placeholder="0"
                />
                <span className="pos-theme-hint">{packSize} {posUnitNoun(product, packSize)} each</span>
              </label>
            )}

            <label className="pos-field">
              <span>Loose {posUnitNoun(product, 2)} that arrived</span>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                disabled={saving}
                placeholder="0"
              />
              {/* THE TWO FIELDS ADD UP. Said out loud because a form with
                  "packs" above "pieces" reads as a choice between them, and a
                  delivery of ten boxes AND ten singles is ordinary. */}
              <span className="pos-theme-hint">
                {packSize > 0
                  ? `Singles not in a ${posPackNoun(product, 1)} — fill in both if both arrived`
                  : 'How many arrived'}
              </span>
            </label>
          </div>

          {/* The running total, so the result is visible before it is
              committed to — a delivery of "3 boxes and 10" is a number nobody
              should have to work out in their head to check. */}
          {arriving > 0 && (
            <div className="pos-replenish-preview">
              <p>
                Adding <strong>{describePosUnits(arriving, product)}</strong> to the storeroom.
              </p>
              <p className="pos-replenish-after">
                Reserve becomes <strong>{describePosUnits(current + arriving, product)}</strong>
              </p>
            </div>
          )}

          {error && <p className="pos-error">{error}</p>}
          {saving && <div className="pos-modal-saving">{spinnerRing('Saving…')}</div>}
        </div>

        <footer className="pos-modal-foot">
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="pos-btn pos-btn-primary" onClick={handleAdd} disabled={saving || arriving < 1}>
            {saving ? 'Saving…' : 'Add to reserve'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosAddReserveModal;
